import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import {
  NATEXT,
  s3Url,
  repairReasonKeyMap,
  repairClassKeyMap,
  repairSiteKeyMap,
  taskLogStatusMap,
} from "../../../shared/constants";
import defaultVehicleIcon from "@/shared/img/defaultVehicleIcon.png";
import { useSelector } from "react-redux";
import {
  getAssetConditionOptions,
  getDisplayDateWithTimeNew,
  getDistanceFactorforOdo,
  getDistanceUnit,
} from "../../../shared/helpers";
import { WorkOrderStatus } from "../WorkOrdersTable";
import { LogDiv, ReadOnlyIssueTag } from "./LogsPopup";
import { colorLightGray } from "../../../utils/palette";
import { AssetConditionValue } from "../../../shared/components/AssetConditionDisplay";

const WOPrintComponent = forwardRef((props, ref) => {
  const { t } = useTranslation("common");
  const { distance, currency } = useSelector((state) => state.units);
  const { orgName, address, country, woTimerMode } = useSelector(
    (state) => state.user,
  );
  const users = useSelector((state) => state.users.list);
  const { templates } = useSelector((state) => state.servicesTasks);

  const {
    rowData,
    vehicle,
    serviceTasksToPrint,
    issuesToPrint,
    logsToPrint,
    selectedTechnician,
    timeToComplete,
  } = props;

  const assetConditionOptions = getAssetConditionOptions(t);

  console.log("serviceTasksToPrint: ", serviceTasksToPrint);
  console.log("rowData: ", rowData);
  const {
    wo_id,
    wo_number,
    org_id,
    created_by_name,
    create_date,
    start_date,
    end_date,
    status,
    due_date,
    odo,
    total_time,
    discount,
    tax,
    total,
    po_number,
    invoice_number,
    comments,
    template_id,
    logs,
    repair_reason,
    type,
    repair_site,
    repair_reason_description,
    asset_condition,
  } = rowData;

  const {
    veh_img,
    veh_id,
    name,
    primary_meter,
    license_no,
    vin_no,
    veh_active,
    secondary_status,
    veh_type,
  } = vehicle;
  const distanceFactor = getDistanceFactorforOdo(primary_meter, distance);

  let odoVal = odo;
  if (odo > 0 && odo !== -1000) {
    odoVal = (+odo / distanceFactor)?.toFixed(2);
  }

  const imageUrl = veh_img
    ? `${s3Url}/org_${org_id}/vehicle_${veh_id}/${veh_img}`
    : defaultVehicleIcon;

  let grandTotal = +total - +discount + +tax;

  const formatElapsed = (minutes) => {
    if (!minutes) return "0m";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const getGroupedParts = (batches) => {
    if (!batches?.length) return [];
    const map = {};
    batches.forEach((b) => {
      if (!map[b.part_id]) {
        map[b.part_id] = {
          part_name: b.part_name,
          part_number: b.part_number,
          qty: 0,
        };
      }
      map[b.part_id].qty += +b.unitsConsumed;
    });
    return Object.values(map);
  };

  let vehicleStatus = "active";
  if (veh_active === 0 || veh_active === 2) {
    vehicleStatus = "inactive";
  }
  if (veh_active === 1 && secondary_status === 1) {
    vehicleStatus = "outOfService";
  }

  const vehicleStatusStyles = {
    active: { text: t("active"), color: "green" },
    inactive: { text: t("inactive"), color: colorLightGray },
    outOfService: { text: t("out_of_service"), color: "red" },
  };

  const templateName = template_id
    ? templates?.find((template) => template.value === template_id)?.label
    : "";

  return (
    <div
      ref={ref}
      className="m-5 d-flex flex-column gap-4"
      style={{ fontWeight: 500 }}
    >
      <div className="d-flex justify-content-between">
        <div>
          <p>{orgName}</p>
          <p>{address}</p>
          <p>{country}</p>
        </div>
        <div>
          <p>{`${t("wo_number")} ${wo_number}`}</p>
        </div>
      </div>

      <div>
        <div className="d-flex gap-2 align-items-center">
          <img
            src={imageUrl}
            alt="vehicle-img"
            width={40}
            height={40}
            className="rounded-circle"
          />
          <div>
            <p style={{ fontSize: 16, fontWeight: 500 }}>{name}</p>
            <div className="d-flex gap-1">
              <p>{veh_type}</p>
              <p>-</p>
              <p style={{ color: vehicleStatusStyles[vehicleStatus]?.color }}>
                {vehicleStatusStyles[vehicleStatus]?.text}
              </p>
            </div>
            <div className="d-flex gap-1">
              <span>{t("vin")}:</span>
              <p>{vin_no || NATEXT}</p>
            </div>
            <div className="d-flex gap-1">
              <span>{t("license")}:</span>
              <p>{license_no || NATEXT}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between">
        <div>
          <span>{t("created_by")}</span>
          <p>{created_by_name}</p>
        </div>

        <div>
          <span>{t("created_date")}</span>
          <p>{getDisplayDateWithTimeNew(create_date)}</p>
        </div>

        <div>
          <span>{t("status")}</span>
          <WorkOrderStatus status={status} />
        </div>

        <div>
          <span>{t("due_date")}</span>
          <p>{due_date ? getDisplayDateWithTimeNew(due_date) : NATEXT}</p>
        </div>

        <div>
          <span>{t("accepted_by")}</span>
          <p>{selectedTechnician ? selectedTechnician : NATEXT}</p>
        </div>
      </div>

      <div className="d-flex justify-content-between">
        <div>
          <span>{t("started_on")}</span>
          <p>{start_date ? getDisplayDateWithTimeNew(start_date) : NATEXT}</p>
        </div>

        <div>
          <span>{t("ended_on")}</span>
          <p>{end_date ? getDisplayDateWithTimeNew(end_date) : NATEXT}</p>
        </div>

        <div>
          <span>{t("time_taken_to_complete")}</span>
          <p>{timeToComplete || NATEXT}</p>
        </div>

        <div>
          <span>{t("odometer")}</span>
          <p>
            {odo != -1000
              ? `${odoVal} ${getDistanceUnit(primary_meter)}`
              : NATEXT}
          </p>
        </div>

        <div>
          <span>{t("asset_condition")}</span>
          <div>
            <AssetConditionValue
              option={assetConditionOptions.find(
                (opt) => opt.value === asset_condition,
              )}
            />
          </div>
        </div>
      </div>

      {/* Issues */}
      {issuesToPrint?.length > 0 && (
        <div className="d-flex flex-column gap-2">
          <h5>{t("issues")}</h5>
          <table style={{ width: "100%" }} className="table table-bordered">
            <thead>
              <tr>
                <th>{t("issue")}</th>
                <th>{t("issue_desc")}</th>
              </tr>
            </thead>
            <tbody>
              {issuesToPrint.map((issue) => (
                <tr key={issue.issue_id}>
                  <td>{issue.issue}</td>
                  <td>{issue.issue_desc || NATEXT}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Service Tasks, Discount, Tax, Total */}
      {serviceTasksToPrint?.length > 0 && (
        <div className="d-flex flex-column gap-2">
          <h5>{t("service_tasks")}</h5>
          {templateName && (
            <p>
              {t("template")}: {templateName}
            </p>
          )}
          <table style={{ width: "100%" }} className="table table-bordered">
            <thead>
              <tr>
                <th>{t("service_task_name")}</th>
                <th>{`${t("parts_cost")} (${currency})`}</th>
                <th>{`${t("labor_cost")} (${currency})`}</th>
                <th>{`${t("total")} (${currency})`}</th>
              </tr>
            </thead>
            <tbody>
              {serviceTasksToPrint.map((task) => {
                const groupedParts = getGroupedParts(task.batches);
                const reversedLogs =
                  task.logs?.length > 0 ? [...task.logs].reverse() : [];
                const hasSubSection =
                  ((task.initialTimerStatus === "completed" ||
                    woTimerMode === 2) &&
                    task.initialElapsed > 0) ||
                  groupedParts.length > 0 ||
                  reversedLogs.length > 0 ||
                  task?.comments;
                return (
                  <React.Fragment key={task.service_task_id}>
                    <tr>
                      <td>{task.service_task_name}</td>
                      <td>{task.partsCost || NATEXT}</td>
                      <td>{task.laborCost || NATEXT}</td>
                      <td>{task.totalCost || NATEXT}</td>
                    </tr>
                    {hasSubSection && (
                      <tr>
                        <td colSpan="4">
                          <div
                            className="p-0 m-0"
                            style={{ paddingLeft: "8px" }}
                          >
                            {/* Time taken */}
                            {(task.initialTimerStatus === "completed" ||
                              woTimerMode === 2) &&
                              task.initialElapsed > 0 && (
                                <p
                                  style={{
                                    fontSize: "13px",
                                    marginBottom: "8px",
                                  }}
                                >
                                  {t("time_taken_to_complete")}:{" "}
                                  <strong>
                                    {formatElapsed(task.initialElapsed)}
                                  </strong>
                                </p>
                              )}

                            {/* Parts used */}
                            {groupedParts.length > 0 && (
                              <div style={{ marginBottom: "8px" }}>
                                <p
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    marginBottom: "4px",
                                  }}
                                >
                                  {t("parts_used")}
                                </p>
                                <table
                                  style={{ width: "100%", fontSize: "13px" }}
                                >
                                  <tbody>
                                    {groupedParts.map((part, i) => (
                                      <tr key={i}>
                                        <td>
                                          {part.part_name}
                                          {part.part_number
                                            ? ` (${part.part_number})`
                                            : ""}
                                        </td>
                                        <td
                                          style={{ textAlign: "right" }}
                                        >{`${t("qty")}: ${part.qty}`}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Notes */}
                            {task.comments && (
                              <p
                                style={{
                                  fontSize: "13px",
                                  marginBottom: "8px",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {t("notes")}: {task.comments}
                              </p>
                            )}

                            {/* Task logs */}
                            {reversedLogs.length > 0 && (
                              <div>
                                <p
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    marginBottom: "4px",
                                  }}
                                >
                                  {t("task_log")}
                                </p>
                                {reversedLogs.map((log, idx) => {
                                  const logUser = users?.find(
                                    (u) => u.user_id === log.user_id,
                                  );
                                  const userName =
                                    logUser?.name || t("unassigned_user");
                                  const logDate = new Date(log.time * 1000);
                                  const timeStr = logDate.toLocaleTimeString(
                                    [],
                                    { hour: "2-digit", minute: "2-digit" },
                                  );
                                  const dateStr = logDate.toLocaleDateString(
                                    [],
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    },
                                  );
                                  return (
                                    <div
                                      key={idx}
                                      style={{
                                        marginBottom: "8px",
                                        fontSize: "13px",
                                        display: "flex",
                                        gap: "8px",
                                      }}
                                    >
                                      <p style={{ margin: 0 }}>
                                        {t(taskLogStatusMap[log.status])}{" "}
                                        <strong>{userName}</strong>
                                      </p>
                                      <p
                                        style={{
                                          margin: 0,
                                          fontSize: "12px",
                                          //color: "#666",
                                        }}
                                      >
                                        {dateStr}, {timeStr}
                                      </p>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <div className="align-self-end" style={{ minWidth: "20%" }}>
            <div className="d-flex justify-content-between">
              <span>{`${t("discount")}: `}</span>
              <p>{discount ? `${discount} ${currency}` : NATEXT}</p>
            </div>
            <div className="d-flex justify-content-between">
              <span>{`${t("tax")}: `}</span>
              <p>{tax ? `${tax} ${currency}` : NATEXT}</p>
            </div>
            <div className="d-flex justify-content-between">
              <span>{`${t("grand_total")}: `}</span>
              <p>{grandTotal ? `${grandTotal} ${currency}` : NATEXT}</p>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex gap-4">
        <p>{`${t("invoice_no")}: ${invoice_number || NATEXT}`}</p>
        <p>{`${t("po_number")}: ${po_number || NATEXT}`}</p>
      </div>

      {(repair_reason_description ||
        repairClassKeyMap[type] ||
        repairSiteKeyMap[repair_site]) && (
        <div>
          <h5>{t("repair_details")}</h5>
          <div className="d-flex gap-4">
            {repair_reason_description && (
              <div>
                <span>{t("repair_reason")}</span>
                <p>{repair_reason_description}</p>
              </div>
            )}
            {repairClassKeyMap[type] && (
              <div>
                <span>{t("repair_class")}</span>
                <p>{t(repairClassKeyMap[type])}</p>
              </div>
            )}
            {repair_site !== -1 && repairSiteKeyMap[repair_site] && (
              <div>
                <span>{t("repair_site")}</span>
                <p>{t(repairSiteKeyMap[repair_site])}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div>
        <span>{t("notes_tv")}</span>
        <p>{comments || NATEXT}</p>
      </div>

      {/* Logs */}
      {logsToPrint?.length > 0 && (
        <div className="d-flex flex-column gap-2">
          <h5>{t("notes_prog_updates")}</h5>
          {logsToPrint.map((log) => (
            <LogDiv key={log.log_id}>
              <div className="d-flex flex-column gap-1">
                <div className="d-flex justify-content-between">
                  <p
                    style={{
                      fontStyle: "italic",
                      fontSize: "12px",
                      fontWeight: 500,
                      alignSelf: "center",
                    }}
                  >{`${getDisplayDateWithTimeNew(log.time_stamp)} - ${
                    log.user
                  }`}</p>

                  {(log.log_type == 1 ||
                    log.log_type == 2 ||
                    log.log_type == 3 ||
                    log.log_type == 4 ||
                    log.log_type == 5 ||
                    log.log_type == 6) && (
                    <div>
                      {log.log_type == 1 && (
                        <div className="d-flex gap-1">
                          <i
                            className="fi fi-rr-add"
                            style={{ position: "relative", top: "2px" }}
                          ></i>
                          <span>{t("wo_created")}</span>
                        </div>
                      )}
                      {log.log_type == 2 && (
                        <div className="d-flex gap-1">
                          <i
                            className="fi fi-rs-check-circle"
                            style={{ position: "relative", top: "2px" }}
                          ></i>
                          <span>{t("wo_accepted")}</span>
                        </div>
                      )}
                      {log.log_type == 3 && (
                        <div className="d-flex gap-1">
                          <i
                            className="fi fi-rr-pause-circle"
                            style={{ position: "relative", top: "2px" }}
                          ></i>
                          <span>{t("wo_paused")}</span>
                        </div>
                      )}
                      {log.log_type == 4 && (
                        <div className="d-flex gap-1">
                          <i
                            className="fi fi-rr-play-circle"
                            style={{ position: "relative", top: "2px" }}
                          ></i>
                          <span>{t("wo_resumed")}</span>
                        </div>
                      )}
                      {log.log_type == 5 && (
                        <div className="d-flex gap-1 align-items-center">
                          <i
                            className="fi fi-rr-stop-circle"
                            style={{ position: "relative", top: "2px" }}
                          ></i>
                          <span>{t("wo_closed")}</span>
                        </div>
                      )}
                      {log.log_type == 6 && (
                        <div className="d-flex gap-1 align-items-center">
                          <i
                            className="fi fi-rr-play-circle"
                            style={{ position: "relative", top: "2px" }}
                          ></i>
                          <span>{t("wo_reopened")}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {log?.message && <p>{log.message}</p>}
                {log?.log_type === 0 &&
                  log?.linkedIssues &&
                  log?.linkedIssues?.length > 0 && (
                    <div className="d-flex gap-2 flex-wrap">
                      {log.linkedIssues.map((issueName, i) => (
                        <ReadOnlyIssueTag key={i}>
                          <span>{issueName}</span>
                        </ReadOnlyIssueTag>
                      ))}
                    </div>
                  )}
              </div>
            </LogDiv>
          ))}
        </div>
      )}
    </div>
  );
});

export default WOPrintComponent;
